import { CostCenter, Employee, PurchaseOrderStatusEnum } from "@finway-group/shared/lib/models"
import { PURCHASE_ORDER_STATUSES_PRE_APPROVAL } from "@finway-group/shared/lib/models/purchaseOrder/purchaseOrderStatus.enum"
import { TFunction } from "i18next"
import { NO_TAX_RATE } from "Shared/config/consts"
import { AuthzService } from "Shared/services"
import { ILineItem } from "../LineItems.interfaces"
import { ProcurementTableColumn, PurchaseOrder, PurchaseOrderFormValues, PurchaseOrderTableColumn } from "../PurchaseOrder.interfaces"

export type GenerateColumnsProps<T> = {
    updateRecord: (record: T) => void
    deleteRecord: (key: string) => void
    getRecord: (record: T) => void
    t: TFunction
}

export type GenerateProcurementColumnsProps<T> = {
    updateRecord: (record: T) => void
    t: TFunction
}

export const resolveTax = (li: Array<ILineItem>) => {
    const taxRates = li.map((item) => item.taxRate?._id).filter((id) => id !== undefined)
    const uniqueTaxRates = [...new Set(taxRates)]

    if (uniqueTaxRates.length === 1) {
        return uniqueTaxRates[0]
    }

    return NO_TAX_RATE
}

export const addCostCenterResponsible = ({
    filteredEmployees,
    employees,
    costCenters,
    purchaseOrder,
}: {
    filteredEmployees: Array<Employee>
    employees: Array<Employee>
    costCenters: Array<CostCenter>
    purchaseOrder: PurchaseOrder
}) => {
    const costCenter = costCenters.find((costCenter: CostCenter) => costCenter._id === purchaseOrder.lineItems[0]?.costCenter)
    const isResponsibleUserInTheList = filteredEmployees.find((employee) => employee.id === costCenter?.responsibleUser)

    const responsibleUserHasRightsToApprove = costCenter?.responsibleUser && AuthzService.isUserPurchaseOrderApprover(costCenter?.responsibleUser)

    if (costCenter && !isResponsibleUserInTheList && responsibleUserHasRightsToApprove) {
        const responsibleUser = employees.find((employee) => employee.id === costCenter.responsibleUser)

        if (responsibleUser && !responsibleUser?.activeCompanyProfile.deleted) {
            filteredEmployees.push(responsibleUser)
        }
    }

    return filteredEmployees
}

export const mapPurchaseOrderToTable = (po: PurchaseOrder): PurchaseOrderTableColumn => ({
    key: po._id,
    name: po.purchaseOrderNumber,
    description: po.description,
    requester: `${po.requestedBy.firstName} ${po.requestedBy.lastName}`,
    vendor: po.vendor.name!,
    dateOfIssue: po.dateOfIssue,
    deliveryStatus: po.deliveryStatus,
    invoicedStatus: po.invoicedStatus,
    amount: po.totalGrossPrice,
    status: po.state,
    isService: po.isService,
    requestedBy: po.requestedBy,
    state: po.state,
    currency: po.currency,
})

export const mapProcurementToTable = (po: PurchaseOrder): ProcurementTableColumn => ({
    key: po._id,
    name: po.purchaseOrderNumber,
    tags: po.tags,
    totalGrossPrice: po.totalGrossPrice,
    currency: po.currency,
    isService: po.isService,
    purchaseOrderNumber: po.purchaseOrderNumber,
    _id: po._id,
    description: po.description,
    amount: po.totalGrossPrice,
})
export const createDefaultLineItem = (prev?: Partial<PurchaseOrderFormValues["lineItems"][number]>): Omit<Partial<PurchaseOrderFormValues["lineItems"][number]>, "_id"> => ({
    quantity: 1,
    name: "",
    unitOfMeasure: "",
    unitPrice: 0,
    costCenter2: prev?.costCenter2,
    costCenter: prev?.costCenter,
    expenseAccount: prev?.expenseAccount,
    taxRate: {
        _id: prev?.taxRate?._id || NO_TAX_RATE,
    },
    totalTaxPrice: 0,
    totalNetPrice: 0,
    totalGrossPrice: 0,
    materialCode: "",
    link: "",
})

export const isCardMissing = (po: PurchaseOrder) => po.state.currentState === PurchaseOrderStatusEnum.FULFILLMENT && po.createCard && !po.cardId

export const calculateLargestCCByShare = (lineItems: Array<Pick<ILineItem, "costCenter" | "totalGrossPrice">>) => {
    // group all line items by cost center
    const groupedLineItems = lineItems.reduce((acc: Record<string, Array<ILineItem>>, item: ILineItem) => {
        acc[item.costCenter] = [...(acc[item.costCenter] || []), item]
        return acc
    }, {})

    // get Max gross value
    const maxGrossValue = Math.max(
        ...Object.values(groupedLineItems).map((items: Array<ILineItem>) => items.reduce((acc: number, item: ILineItem) => acc + item.totalGrossPrice, 0)),
    )

    // get cc that has max gross value
    const maxGrossCc = Object.keys(groupedLineItems).find((cc) => groupedLineItems[cc].reduce((acc: number, item: ILineItem) => acc + item.totalGrossPrice, 0) === maxGrossValue)

    return maxGrossCc ?? ""
}

export const isPurchaseOrderApproverVisible = (po: PurchaseOrder) => PURCHASE_ORDER_STATUSES_PRE_APPROVAL.includes(po.state.currentState)
